import { DOCUMENT } from '@angular/common';
import { Injectable, Inject, OnDestroy } from '@angular/core';
import { AppBarThemeColor } from '@progress/kendo-angular-navigation';
import { BehaviorSubject, Observable } from 'rxjs';

export type ThemeMode = 'light' | 'dark' | 'system';

@Injectable({
    providedIn: 'root'
})
export class ThemeSwitcherService implements OnDestroy {
    private readonly storageKey = 'user-theme';
    private readonly themeSubject: BehaviorSubject<'light' | 'dark'>;
    public readonly themeChanges: Observable<'light' | 'dark'>;

    /** The resolved theme actually applied (always 'light' or 'dark') */
    public theme: 'light' | 'dark' = 'light';
    /** The user-selected mode (can be 'system') */
    public themeModeSetting: ThemeMode = 'light';
    public themeMode: AppBarThemeColor;

    private systemMediaQuery: MediaQueryList | null = null;
    private systemThemeListener: ((e: MediaQueryListEvent) => void) | null = null;

    constructor(@Inject(DOCUMENT) private document: Document) {
        const initialMode = this.loadStoredMode();
        this.themeModeSetting = initialMode;
        const resolvedTheme = this.resolveTheme(initialMode);
        this.theme = resolvedTheme;
        this.themeMode = this.mapStringToAppBarThemeColor(resolvedTheme);
        this.themeSubject = new BehaviorSubject<'light' | 'dark'>(resolvedTheme);
        this.themeChanges = this.themeSubject.asObservable();
        this.applyTheme(resolvedTheme);

        if (initialMode === 'system') {
            this.listenToSystemTheme();
        }
    }

    ngOnDestroy(): void {
        this.stopListeningToSystemTheme();
    }

    toggleTheme(): void {
        const nextTheme = this.theme === 'light' ? 'dark' : 'light';
        this.setMode(nextTheme);
    }

    /** Set mode to 'light', 'dark', or 'system' */
    setMode(mode: ThemeMode): void {
        this.themeModeSetting = mode;
        localStorage.setItem(this.storageKey, mode);

        if (mode === 'system') {
            this.listenToSystemTheme();
            const resolved = this.getSystemTheme();
            this.setResolvedTheme(resolved);
        } else {
            this.stopListeningToSystemTheme();
            this.setResolvedTheme(mode);
        }
    }

    /** @deprecated Use setMode() instead */
    setTheme(theme: 'light' | 'dark'): void {
        this.setMode(theme);
    }

    get currentTheme(): 'light' | 'dark' {
        return this.theme;
    }

    private setResolvedTheme(theme: 'light' | 'dark'): void {
        this.theme = theme;
        this.themeSubject.next(theme);
        this.applyTheme(theme);
    }

    private resolveTheme(mode: ThemeMode): 'light' | 'dark' {
        if (mode === 'system') {
            return this.getSystemTheme();
        }
        return mode;
    }

    private getSystemTheme(): 'light' | 'dark' {
        if (typeof window !== 'undefined' && window.matchMedia) {
            return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
        }
        return 'light';
    }

    private listenToSystemTheme(): void {
        this.stopListeningToSystemTheme();
        if (typeof window !== 'undefined' && window.matchMedia) {
            this.systemMediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
            this.systemThemeListener = (e: MediaQueryListEvent) => {
                if (this.themeModeSetting === 'system') {
                    this.setResolvedTheme(e.matches ? 'dark' : 'light');
                }
            };
            this.systemMediaQuery.addEventListener('change', this.systemThemeListener);
        }
    }

    private stopListeningToSystemTheme(): void {
        if (this.systemMediaQuery && this.systemThemeListener) {
            this.systemMediaQuery.removeEventListener('change', this.systemThemeListener);
            this.systemMediaQuery = null;
            this.systemThemeListener = null;
        }
    }

    private applyTheme(theme: 'light' | 'dark'): void {
        const body = this.document?.body;
        const root = this.document?.documentElement;
        if (body) {
            body.classList.toggle('dark', theme === 'dark');
            body.classList.toggle('theme-dark', theme === 'dark');
        }
        if (root) {
            root.classList.toggle('dark', theme === 'dark');
            root.classList.toggle('theme-dark', theme === 'dark');
        }
        this.updateThemeLink(theme);
        this.themeMode = this.mapStringToAppBarThemeColor(theme);
    }

    private updateThemeLink(theme: 'light' | 'dark'): void {
        const themeLink = this.document?.getElementById('kendo-theme') as HTMLLinkElement | null;
        if (!themeLink) {
            return;
        }
        const lightHref = themeLink.getAttribute('data-light-theme');
        const darkHref = themeLink.getAttribute('data-dark-theme');
        if (theme === 'dark' && darkHref) {
            themeLink.href = darkHref;
        } else if (theme === 'light' && lightHref) {
            themeLink.href = lightHref;
        }
    }

    private loadStoredMode(): ThemeMode {
        const stored = localStorage.getItem(this.storageKey) as ThemeMode | null;
        if (stored === 'dark' || stored === 'light' || stored === 'system') {
            return stored;
        }
        return 'light';
    }

    public mapStringToAppBarThemeColor(theme: string): AppBarThemeColor {
        // Define the valid theme colors
        const validThemeColors: AppBarThemeColor[] = ["light", "dark", "primary", "inherit"];
      
        // Check if the provided theme is valid, otherwise return a default value
        if (validThemeColors.includes(theme as AppBarThemeColor)) {
          return theme as AppBarThemeColor;
        } else {
          // Handle invalid theme case, defaulting to "light"
          console.warn(`Invalid theme: ${theme}. Defaulting to "light".`);
          return "light";
        }
      }
}
